import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { AnalyticsReportDefinition, AnalyticsShopSpecificFilters } from '../../../types'
import {
  getCurrentFilterValues,
  getFilterState,
  getFiltersForAPI,
  getFiltersForVisibility,
  updateFilterParam,
} from './utils/filterUtils'

type UseDynamicFiltersParamsParams = {
  reportDefinition: AnalyticsReportDefinition | undefined
  shopSpecificFilters: AnalyticsShopSpecificFilters | undefined
}

export const useDynamicFiltersParams = ({
  reportDefinition,
  shopSpecificFilters,
}: UseDynamicFiltersParamsParams) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const filterGroups = useMemo(() => {
    if (!reportDefinition?.filterGroups) return []

    // Combine static filters from reportDefinition with dynamic filters from shopSpecificFilters
    return reportDefinition.filterGroups.map((group) => {
      if (group.filterType === 'dynamic' && shopSpecificFilters) {
        // For dynamic filters, use the shop-specific version
        const dynamicGroup = shopSpecificFilters.find(
          (dynamicGroup) => dynamicGroup.key === group.key,
        )

        return dynamicGroup || group
      }

      // For static filters, use the original group
      return group
    })
  }, [reportDefinition?.filterGroups, shopSpecificFilters])

  const currentFilterValues = useMemo(() => {
    if (!filterGroups.length) return {}

    return getCurrentFilterValues(filterGroups, searchParams)
  }, [filterGroups, searchParams])

  const currentFilters = useMemo(() => {
    if (!filterGroups.length) return []

    return getFilterState(filterGroups, currentFilterValues)
  }, [filterGroups, currentFilterValues])

  const filtersForAPI = useMemo(() => {
    if (!filterGroups.length) return {}

    return getFiltersForAPI(currentFilterValues)
  }, [filterGroups.length, currentFilterValues])

  const hasActiveFilters = useMemo(() => {
    if (!filterGroups.length) return false

    return filterGroups.some((group) => {
      const values = currentFilterValues[group.key] || []

      if (group.selectionType === 'multiple') {
        return values.length > 0
      }

      // single selection: active if current differs from default
      const defaultFilterKeys = group.filters
        .filter((filter) => filter.default_value)
        .map((filter) => filter.key)

      const selectedValuesSet = new Set(values)
      const defaultValuesSet = new Set(defaultFilterKeys)
      if (selectedValuesSet.size !== defaultValuesSet.size) return true
      for (const value of selectedValuesSet) {
        if (!defaultValuesSet.has(value)) return true
      }

      return false
    })
  }, [filterGroups, currentFilterValues])

  const filtersForVisibility = useMemo(() => {
    if (!filterGroups.length) return {}

    return getFiltersForVisibility(filterGroups, currentFilterValues, hasActiveFilters)
  }, [filterGroups, currentFilterValues, hasActiveFilters])

  const hasFilters = useMemo(
    () => !!reportDefinition?.filterGroups,
    [reportDefinition?.filterGroups],
  )

  const handleUpdateFilterParam = useCallback(
    (groupKey: string, filterKey: string, checked: boolean, groupType?: 'single' | 'multiple') => {
      updateFilterParam(
        groupKey,
        filterKey,
        checked,
        groupType || 'multiple',
        searchParams,
        setSearchParams,
      )
    },
    [searchParams, setSearchParams],
  )

  const handleFilterChange = useCallback(
    (groupTitle: string, itemValue: string, checked: boolean) => {
      if (!filterGroups.length) return

      const group = filterGroups.find((g) => g.label === groupTitle)
      if (!group) return

      handleUpdateFilterParam(group.key, itemValue, checked, group.selectionType)
    },
    [filterGroups, handleUpdateFilterParam],
  )

  return {
    hasFilters,
    hasActiveFilters,
    currentFilters,
    filtersForAPI,
    filtersForVisibility,
    updateFilterParam: handleUpdateFilterParam,
    handleFilterChange,
  }
}
